import { useEffect, useState } from 'react';
import {
  Button,
  Input,
  Modal,
  Select,
  Textarea,
  type SelectOption,
} from '../../ui';
import { MultiPicker } from '../common/MultiPicker';
import {
  blankDecisionDraft,
  draftFromDecision,
  listToInput,
  listToLines,
  parseLines,
  parseList,
  validateDecisionDraft,
} from '../../features/decisions/decisionModel';
import type { Decision, DecisionDraft } from '../../features/decisions/types';
import type { GoalSummary } from '../../features/goals/types';
import type { WorkstreamSummary } from '../../features/workstreams/types';
import type { Task } from '../../features/tasks/types';
import type { ServiceSummary } from '../../features/services/types';
import type { TeamDocumentSummary } from '../../features/documents/types';

export interface DecisionModalProps {
  open: boolean;
  onClose: () => void;
  defaultOwner: string;
  initialDecision?: Decision;
  goals: GoalSummary[];
  workstreams: WorkstreamSummary[];
  tasks: Task[];
  services: ServiceSummary[];
  documents: TeamDocumentSummary[];
  serviceTitles: Map<string, string>;
  storyTitles: Map<string, string>;
  onSubmit: (draft: DecisionDraft) => Promise<unknown>;
}

/**
 * Create / edit a Decision. This is the durable "why" record — the form leads
 * with the decision, the situation, the options weighed, what was chosen and
 * why, then who + when + what it affects.
 */
export function DecisionModal({
  open,
  onClose,
  defaultOwner,
  initialDecision,
  goals,
  workstreams,
  tasks,
  services,
  documents,
  serviceTitles,
  storyTitles,
  onSubmit,
}: DecisionModalProps) {
  const [draft, setDraft] = useState<DecisionDraft>(blankDecisionDraft());
  const [optionsText, setOptionsText] = useState('');
  const [participantsText, setParticipantsText] = useState('');
  const [problems, setProblems] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const base = initialDecision
      ? draftFromDecision(initialDecision)
      : blankDecisionDraft(defaultOwner);
    setDraft(base);
    setOptionsText(listToLines(base.optionsConsidered));
    setParticipantsText(listToInput(base.participants));
    setProblems([]);
  }, [open, initialDecision, defaultOwner]);

  const set = <K extends keyof DecisionDraft>(key: K, value: DecisionDraft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const goalOptions: SelectOption[] = goals.map((g) => ({ value: g.id, label: g.title || g.id }));
  const workstreamOptions: SelectOption[] = workstreams.map((w) => ({ value: w.id, label: w.title || w.id }));
  const serviceOptions: SelectOption[] = services.map((s) => ({ value: s.id, label: s.name || s.id }));
  const storyOptions: SelectOption[] = tasks.map((t) => ({ value: t.id, label: t.title || t.id }));
  const docOptions: SelectOption[] = documents.map((d) => ({ value: d.id, label: d.title || d.id }));

  const buildDraft = (): DecisionDraft => ({
    ...draft,
    decision: draft.decision.trim(),
    context: draft.context.trim(),
    decisionMade: draft.decisionMade.trim(),
    why: draft.why.trim(),
    owner: draft.owner.trim(),
    optionsConsidered: parseLines(optionsText),
    participants: parseList(participantsText),
  });

  const submit = async () => {
    const next = buildDraft();
    const found = validateDecisionDraft(next);
    if (found.length > 0) {
      setProblems(found);
      return;
    }
    setSaving(true);
    try {
      await onSubmit(next);
      onClose();
    } catch (e) {
      setProblems([e instanceof Error ? e.message : 'Failed to save decision.']);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => !saving && onClose()}
      size="xl"
      title={initialDecision ? 'Edit decision' : 'Record decision'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button loading={saving} onClick={submit}>
            {initialDecision ? 'Save changes' : 'Record decision'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {problems.length > 0 && (
          <ul className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {problems.map((p) => (
              <li key={p}>• {p}</li>
            ))}
          </ul>
        )}

        <Input
          label="Decision (required)"
          placeholder="The decision, as a short headline"
          value={draft.decision}
          onChange={(e) => set('decision', e.target.value)}
        />
        <Textarea
          label="Context"
          hint="The situation / problem that forced the decision."
          rows={2}
          value={draft.context}
          onChange={(e) => set('context', e.target.value)}
        />
        <Textarea
          label="Options considered"
          hint="One option per line."
          rows={3}
          value={optionsText}
          onChange={(e) => setOptionsText(e.target.value)}
        />
        <Textarea
          label="Decision made (required)"
          hint="What was actually chosen."
          rows={2}
          value={draft.decisionMade}
          onChange={(e) => set('decisionMade', e.target.value)}
        />
        <Textarea
          label="Why (required)"
          hint="Why this option over the others — the durable rationale."
          rows={2}
          value={draft.why}
          onChange={(e) => set('why', e.target.value)}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Owner (required)"
            value={draft.owner}
            onChange={(e) => set('owner', e.target.value)}
          />
          <Input
            label="Participants"
            hint="Comma-separated aliases"
            value={participantsText}
            onChange={(e) => setParticipantsText(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            type="date"
            label="Date (required)"
            value={draft.date}
            onChange={(e) => set('date', e.target.value)}
          />
          <Input
            type="date"
            label="Revisit date"
            hint="Optional — schedule a re-evaluation."
            value={draft.revisitDate ?? ''}
            onChange={(e) => set('revisitDate', e.target.value || undefined)}
          />
        </div>

        <MultiPicker
          label="Affected services"
          options={serviceOptions}
          value={draft.affectedServices}
          onChange={(ids) => set('affectedServices', ids)}
          labelFor={(id) => serviceTitles.get(id) ?? id}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            label="Related goal"
            placeholder="None"
            value={draft.relatedGoalId ?? ''}
            options={goalOptions}
            onChange={(e) => set('relatedGoalId', e.target.value || undefined)}
          />
          <Select
            label="Related workstream"
            placeholder="None"
            value={draft.relatedWorkstreamId ?? ''}
            options={workstreamOptions}
            onChange={(e) => set('relatedWorkstreamId', e.target.value || undefined)}
          />
        </div>

        <Select
          label="Related document"
          placeholder="None"
          value={draft.relatedDocId ?? ''}
          options={docOptions}
          onChange={(e) => set('relatedDocId', e.target.value || undefined)}
        />

        <MultiPicker
          label="Related stories"
          options={storyOptions}
          value={draft.relatedStoryIds}
          onChange={(ids) => set('relatedStoryIds', ids)}
          labelFor={(id) => storyTitles.get(id) ?? id}
        />
      </div>
    </Modal>
  );
}

export default DecisionModal;
